import React, { FC, useEffect, useRef, useState } from 'react'

import { FilterAccordion, useOnClickOutside } from '@avantstay/backoffice-core'

import { RangeFilter } from '@new/__global/components/RangeFilter'

import { FiltersSharedProps } from '../Filters.types'

export interface RangeValuesProps {
  fromRangeValue?: string
  toRangeValue?: string
}

export interface RangeValuesLabelProps extends RangeValuesProps {
  formatValue?: (value?: string) => void
}

interface RangeValuesFilterProps extends FiltersSharedProps {
  title: string
  fromInputTypeIndicator?: string
  toInputTypeIndicator?: string
  maxToValue?: number
  minFromValue?: number
  rangeValues: RangeValuesProps
  onChange: ({ fromRangeValue, toRangeValue }: RangeValuesProps) => void
  shouldDisplayFiltersOnSameLine?: boolean
  className?: string
  placeholder?: { from?: string; to?: string }
  isNegativeValueAllowed?: boolean
  invertRange?: boolean
  onChangeInvertRange?: (inverted: boolean) => void
}

export const RangeValuesFilter: FC<RangeValuesFilterProps> = ({
  title,
  fromInputTypeIndicator = '$',
  toInputTypeIndicator = '$',
  maxToValue,
  minFromValue,
  rangeValues,
  isSidebarFilter,
  onChange,
  shouldDisplayFiltersOnSameLine,
  className,
  placeholder,
  showFilterAsColumn,
  onShowFilterAsColumn,
  isNegativeValueAllowed,
  invertRange,
  onChangeInvertRange,
}) => {
  const initialFrom = rangeValues.fromRangeValue
  const initialTo = rangeValues.toRangeValue
  const [fromValue, setFromValue] = useState<string | undefined>(initialFrom)
  const [toValue, setToValue] = useState<string | undefined>(initialTo)

  const wrapperRef = useRef<HTMLDivElement>(null)

  const isWithinValidRangeValue = toValue && fromValue ? Number(toValue) >= Number(fromValue) : true
  const isWithinValidRange = maxToValue ? Number(maxToValue) >= Number(toValue) : true

  const handleSubmitValue = () => {
    const adjustedFromValue = fromValue === undefined && toValue !== undefined ? '0' : fromValue

    if (isWithinValidRange && isWithinValidRangeValue) {
      onChange({
        fromRangeValue: adjustedFromValue,
        toRangeValue: toValue,
      })
    }
  }

  useOnClickOutside(wrapperRef, handleSubmitValue)

  useEffect(() => {
    if (isSidebarFilter) {
      handleSubmitValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromValue, toValue, isSidebarFilter])

  // This clears selection when we run resetFilters
  useEffect(() => {
    if (initialFrom === undefined && initialTo === undefined) {
      setFromValue(undefined)
      setToValue(undefined)
    }
  }, [initialFrom, initialTo])

  const handleOnClear = () => {
    setFromValue(undefined)
    setToValue(undefined)
    onChange({ fromRangeValue: undefined, toRangeValue: undefined })
  }

  const filterFromValue =
    fromValue !== undefined && fromValue !== '' ? `${fromInputTypeIndicator} ${fromValue}` : ''
  const filterToValue =
    toValue !== undefined && toValue !== '' ? `${toInputTypeIndicator} ${toValue}` : ''
  const filterValue =
    (fromValue !== undefined && fromValue !== '') || (toValue !== undefined && toValue !== '')
      ? `${filterFromValue} ${filterToValue}`
      : undefined

  return (
    <FilterAccordion
      title={title}
      isSidebarFilter={isSidebarFilter}
      ref={wrapperRef}
      filterValue={filterValue}
      onClearFilter={handleOnClear}
      onShowFilterAsColumn={onShowFilterAsColumn}
      showFilterAsColumn={showFilterAsColumn}
    >
      <RangeFilter
        fromValue={fromValue}
        toValue={toValue}
        minFromValue={minFromValue}
        maxToValue={maxToValue}
        fromInputTypeIndicator={fromInputTypeIndicator}
        toInputTypeIndicator={toInputTypeIndicator}
        onChangeFromValue={event => setFromValue(event.currentTarget.value)}
        onChangeToValue={event => setToValue(event.currentTarget.value)}
        shouldDisplayFiltersOnSameLine={shouldDisplayFiltersOnSameLine}
        className={className}
        placeholder={placeholder}
        isNegativeValueAllowed={isNegativeValueAllowed}
        invertRange={invertRange}
        onChangeInvertRange={onChangeInvertRange}
      />
    </FilterAccordion>
  )
}
